import { useTranslation } from 'react-i18next';
import { History, Sparkles } from 'lucide-react';

interface DirectSaleResumeDialogProps {
  onResume: () => void;
  onRestart: () => void;
}

/**
 * Asks a returning customer whether to pick up their persisted design.
 *
 * Shown before anything of the old session is rendered or cleared — the
 * decision belongs to the customer, not to whichever code path runs first.
 */
export function DirectSaleResumeDialog({
  onResume,
  onRestart,
}: DirectSaleResumeDialogProps) {
  const { t } = useTranslation();

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="direct-sale-resume-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    >
      <div className="w-full max-w-sm rounded-section bg-paper-2 p-6 ring-1 ring-line">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-100">
          <History className="h-5 w-5 text-primary" />
        </div>

        <h2
          id="direct-sale-resume-title"
          className="mt-3 text-lg font-bold text-ink"
        >
          {t('directSale.resume.title')}
        </h2>
        <p className="mt-1 text-sm text-ink-2">{t('directSale.resume.body')}</p>

        <div className="mt-5 flex flex-col gap-2">
          <button
            type="button"
            onClick={onResume}
            className="flex h-11 items-center justify-center gap-2 rounded-card bg-gradient-to-r from-primary-500 to-primary-600 text-sm font-bold text-white transition-[filter] hover:brightness-110"
          >
            <Sparkles className="h-4 w-4" />
            {t('directSale.resume.continue')}
          </button>
          <button
            type="button"
            onClick={onRestart}
            className="flex h-11 items-center justify-center rounded-card border border-line text-sm font-medium text-ink-2 transition-colors hover:border-primary hover:text-primary"
          >
            {t('directSale.resume.restart')}
          </button>
        </div>
      </div>
    </div>
  );
}
